import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Mail, Link2, Phone, Globe, X, Loader2, Inbox } from "lucide-react";
import { api } from "@/lib/api";

const STATUSES = ["New", "Contacted", "Proposal Sent", "Negotiating", "Approved",
  "Campaign Active", "Completed", "Declined", "Archived"];
const PLACEMENTS = ["Homepage Hero", "Homepage Mid Banner", "Sidebar Square", "In-Feed Square",
  "Multiple Placements", "Custom Campaign", "Sponsored Content", "Not Sure — Recommend Something"];
const STATUS_STYLE = {
  New: "bg-klyroo-red/15 text-klyroo-red", Contacted: "bg-amber-400/15 text-amber-300",
  "Proposal Sent": "bg-sky-400/15 text-sky-300", Negotiating: "bg-violet-400/15 text-violet-300",
  Approved: "bg-klyroo-cyan/15 text-klyroo-cyan", "Campaign Active": "bg-emerald-400/15 text-emerald-300",
  Completed: "bg-teal-400/15 text-teal-300", Declined: "bg-slate-500/15 text-slate-400",
  Archived: "bg-white/5 text-slate-500",
};
const inputCls = "bg-klyroo-surface border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-klyroo-cyan/50";

export function AdvertisingInbox({ onChange }) {
  const [items, setItems] = useState(null);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState("All");
  const [placement, setPlacement] = useState("All");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");
  const [active, setActive] = useState(null);

  const load = useCallback(() => {
    setItems(null);
    api.adminAdvertising({ status, placement, search, sort })
      .then((d) => { setItems(d.items); setCounts(d.counts || {}); })
      .catch(() => setItems([]));
  }, [status, placement, search, sort]);
  useEffect(() => { const t = setTimeout(load, search ? 300 : 0); return () => clearTimeout(t); }, [load, search]);

  const fmt = (d) => d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "";

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search company, contact, email…" data-testid="adv-search" className={`${inputCls} pl-3 w-64`} />
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="adv-filter-status" className={inputCls}>
          <option value="All">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}{counts[s] != null ? ` (${counts[s]})` : ""}</option>)}
        </select>
        <select value={placement} onChange={(e) => setPlacement(e.target.value)} data-testid="adv-filter-placement" className={inputCls}>
          <option value="All">All placements</option>
          {PLACEMENTS.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="adv-sort" className={inputCls}>
          <option value="newest">Newest</option>
          <option value="oldest">Oldest</option>
        </select>
      </div>

      {!items ? <div className="py-16 text-center text-slate-500"><Loader2 className="animate-spin mx-auto" /></div>
        : items.length === 0 ? (
          <div className="py-16 text-center text-slate-500" data-testid="adv-empty"><Inbox className="mx-auto mb-2" /> No advertising requests{status !== "All" || placement !== "All" || search ? " match these filters." : " yet."}</div>
        ) : (
          <div className="space-y-2" data-testid="adv-list">
            {items.map((r) => (
              <button key={r.id} onClick={() => setActive(r)} data-testid={`adv-row-${r.id}`}
                className="w-full text-left flex items-center gap-3 card-surface rounded-xl px-4 py-3 hover:bg-white/[0.06] transition">
                <span className="flex-1 min-w-0">
                  <span className="text-white text-sm font-medium truncate block">{r.company_name}</span>
                  <span className="text-xs text-slate-500 truncate block">{r.contact_name}{r.budget_range ? ` · ${r.budget_range}` : ""}</span>
                </span>
                <span className="text-xs text-slate-400 w-40 truncate hidden md:block">{(r.requested_placements || []).join(", ") || "—"}</span>
                {r.website && <Globe size={13} className="text-slate-500 shrink-0" title="Website provided" />}
                {r.reference_url && <Link2 size={13} className="text-slate-500 shrink-0" title="Has reference link" />}
                <span className={`text-[10px] font-mono px-2 py-0.5 rounded shrink-0 ${STATUS_STYLE[r.status]}`}>{r.status}</span>
                <span className="text-xs font-mono text-slate-500 w-14 text-right shrink-0">{fmt(r.created_at)}</span>
              </button>
            ))}
          </div>
        )}

      {active && <DetailPanel r={active} onClose={() => setActive(null)}
        onSaved={() => { setActive(null); load(); onChange && onChange(); }} />}
    </div>
  );
}

function DetailPanel({ r, onClose, onSaved }) {
  const [status, setStatus] = useState(r.status);
  const [notes, setNotes] = useState(r.admin_notes || "");
  const [saving, setSaving] = useState(false);

  const save = async (nextStatus) => {
    setSaving(true);
    try {
      await api.updateAdvertising(r.id, { status: nextStatus || status, admin_notes: notes });
      toast.success("Request updated");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60" onClick={onClose} data-testid="adv-detail">
      <div className="w-full max-w-md h-full bg-klyroo-bg border-l border-white/10 overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-bold text-white truncate">{r.company_name}</h3>
          <button onClick={onClose} data-testid="adv-close" className="text-slate-400 hover:text-white shrink-0 ml-2"><X size={18} /></button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Row label="Contact"><p className="text-slate-300">{r.contact_name || "—"}</p></Row>
          <Row label="Email">{r.email ? <a href={`mailto:${r.email}`} className="text-klyroo-cyan break-all">{r.email}</a> : <p className="text-slate-500">—</p>}</Row>
          <Row label="Phone">{r.phone ? <a href={`tel:${r.phone}`} className="text-slate-300 inline-flex items-center gap-1"><Phone size={12} />{r.phone}</a> : <p className="text-slate-500">—</p>}</Row>
          <Row label="Website">{r.website ? <a href={r.website} target="_blank" rel="noopener noreferrer" className="text-klyroo-cyan break-all inline-flex items-center gap-1"><Globe size={12} />Visit</a> : <p className="text-slate-500">—</p>}</Row>
        </div>

        {(r.requested_placements || []).length > 0 && (
          <Row label="Placements">
            <div className="flex flex-wrap gap-1.5">
              {r.requested_placements.map((p) => <span key={p} className="text-[10px] font-mono px-2 py-0.5 rounded bg-klyroo-cyan/10 text-klyroo-cyan">{p}</span>)}
            </div>
          </Row>
        )}
        <div className="grid grid-cols-2 gap-3">
          {r.campaign_objective && <Row label="Objective"><p className="text-slate-300">{r.campaign_objective}</p></Row>}
          {r.budget_range && <Row label="Budget"><p className="text-slate-300">{r.budget_range}</p></Row>}
          {r.campaign_duration && <Row label="Duration"><p className="text-slate-300">{r.campaign_duration}</p></Row>}
          {r.creative_status && <Row label="Creative"><p className="text-slate-300">{r.creative_status}</p></Row>}
        </div>
        {r.target_audience && <Row label="Target audience"><p className="text-slate-300">{r.target_audience}</p></Row>}
        <Row label="Campaign details"><p className="text-white whitespace-pre-wrap">{r.message}</p></Row>
        {r.reference_url && <Row label="Reference"><a href={r.reference_url} target="_blank" rel="noopener noreferrer" className="text-klyroo-cyan break-all hover:underline inline-flex items-center gap-1"><Link2 size={12} />{r.reference_url}</a></Row>}
        <Row label="Submitted"><p className="text-slate-400 text-sm">{new Date(r.created_at).toLocaleString()}</p></Row>

        <div className="border-t border-white/10 mt-4 pt-4">
          <label className="text-xs font-medium text-slate-300">Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="adv-status-select" className={`${inputCls} w-full mt-1.5`}>
            {STATUSES.map((st) => <option key={st} value={st}>{st}</option>)}
          </select>

          <label className="text-xs font-medium text-slate-300 mt-4 block">Admin notes <span className="text-slate-500">(private)</span></label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} data-testid="adv-notes"
            placeholder="Internal notes — never shown publicly" className={`${inputCls} w-full mt-1.5`} />

          <div className="flex flex-wrap gap-2 mt-4">
            <button disabled={saving} onClick={() => save()} data-testid="adv-save" className="px-4 py-2 rounded-lg bg-klyroo-cyan text-black text-sm font-semibold hover:bg-white disabled:opacity-50">Save</button>
            <button disabled={saving} onClick={() => save("Contacted")} className="px-3 py-2 rounded-lg bg-white/5 text-slate-300 text-sm hover:bg-white/10">Contacted</button>
            <button disabled={saving} onClick={() => save("Proposal Sent")} className="px-3 py-2 rounded-lg bg-white/5 text-slate-300 text-sm hover:bg-white/10">Proposal Sent</button>
            <button disabled={saving} onClick={() => save("Approved")} className="px-3 py-2 rounded-lg bg-white/5 text-slate-300 text-sm hover:bg-white/10">Approve</button>
            <button disabled={saving} onClick={() => save("Declined")} className="px-3 py-2 rounded-lg bg-white/5 text-slate-300 text-sm hover:bg-white/10">Decline</button>
            <button disabled={saving} onClick={() => save("Archived")} className="px-3 py-2 rounded-lg bg-white/5 text-slate-500 text-sm hover:bg-white/10">Archive</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return <div className="mb-3"><div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">{label}</div>{children}</div>;
}
